import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ISkuObject2 } from '../../simulator/innovation/types/sku-popup-model';
import { ENDPOINTS } from 'src/constants';
import { navigateTo } from 'src/utils/navigateHelper';
import {
  fetchLandingPageCards,
  fetchModuleIDData,
  fetchModulesList,
  fetchSelectedSegmentInfo,
  fetchSourceList,
  fetchHomePageMetaData,
  fetchBusinessQuestionPopupData,
  fetchDocument,
  fetchEnhancementPopupData,
  toggleFavoriteItem,
  fetchFavoriteList,
  fetchReleaseNotesData,
  fetchQuickStartUserProfileData,
  fetchQuickStartUserCategoryData,
  fetchAnnouncementMapping,
  fetchRecentlyOpenedList,
  fetchSearchResults,
  recordRecentlyOpenedReport,
  fetchMarketMaturity
} from '../api/landing-api';
import { downloadDocMappingObj } from '../components/landing-wrapper/constant';
import {
  IEnhancementPopupData,
  IMarketMaturityTable,
  IPageListItem,
  IQuickStartObject,
  Announcement,
  IReleaseNotesData,
  IIdentifierObject
} from '../types/landing-api-response.model';
import { ILandingCard } from '../types/landing-card.model';

export interface ILandingState {
  fetchingCards: boolean;
  fetchingSegmentDetails: boolean;
  segmentDetailsData: any;
  fetchingSourceList: boolean;
  isUserGuideDownloading: boolean;
  isLegalDocumentDownloading: boolean;
  fetchingModuleIdData: boolean;
  isFavoriteListFetching: boolean;
  isRecentlyOpenedListFetching: boolean;
  isUserProfileDataFetching: boolean;
  userProfileData: IQuickStartObject[];
  isProductFeatureLoading: boolean;
  productFeatureList: IReleaseNotesData;
  isMarketMaturityDataLoading: boolean;
  marketMaturityCardsData: any;
  isUserCategoryDataFetching: boolean;
  userCategoryData: IQuickStartObject[];
  favouriteListData: {
    data: IPageListItem[];
    identifier: IIdentifierObject;
  };
  recentlyOpenedList: IPageListItem[];
  isAnnouncementLoading: boolean;
  isSearchDataLoading: boolean;
  searchResultsData: {
    data: IPageListItem[];
    identifier: IIdentifierObject;
  };
  searchQueryInput: string;
  announcementData: Announcement[];
  moduleIdData: {};
  sourceFilterData: ISkuObject2[];
  cards: ILandingCard[];
  fetchingHomePageMetaData: boolean;
  homePageMetaData: object;
  fetchingBusinessQuestionPopData: boolean;
  businessQuestionPopupData: any;
  fetchingEnhancementPopupData: boolean;
  enhancementPopupData: IEnhancementPopupData;
  fetchingMarketMaturityData: boolean;
  marketMaturityData: IMarketMaturityTable;
  activeBottomTab: string;
}

export const fetchCards = createAsyncThunk('fetchCards', async () => {
  const response = await fetchLandingPageCards();
  return response;
});

// async slice for getting segment details
export const getSegmentDetails = createAsyncThunk(
  'getSegmentDetails',
  async (sourceID: number) => {
    const response = await fetchSelectedSegmentInfo(sourceID);
    if (response.is_under_maintenance) {
      navigateTo(ENDPOINTS.maintainance)
    }
    return response;
  }
);

// async slice for getting source list
export const getSourceList = createAsyncThunk('getSourceList', async () => {
  const response = await fetchSourceList();
  return response;
});

// async slice for getting home page meta data
export const getHomePageMetaData = createAsyncThunk(
  'getHomePageMetaData',
  async (sourceID: number) => {
    const response = await fetchHomePageMetaData(sourceID);
    return response;
  }
);

// async slice for getting business question popup data
export const getBusinessQuestionPopupData = createAsyncThunk(
  'getBusinessQuestionPopupData',
  async (sourceID: number) => {
    const response = await fetchBusinessQuestionPopupData(sourceID);
    return response;
  }
);

// async slice for getting enhancement popup data
export const getEnhancementPopupData = createAsyncThunk(
  'getEnhancementPopupData',
  async (args: { versionNumber: any; sourceID: any }) => {
    const response = await fetchEnhancementPopupData(
      args.versionNumber,
      args.sourceID
    );
    return response;
  }
);

// async slice for getting module list
export const getModulesList = createAsyncThunk('getModulesList', async () => {
  const response = await fetchModulesList();
  return response;
});

// async slice for downloading user guide
export const getUserGuide = createAsyncThunk('getUserGuide', async () => {
  const response = await fetchDocument({
    url: downloadDocMappingObj.tool_guide.url,
    fileName: downloadDocMappingObj.tool_guide.file_name,
    fileExtension: downloadDocMappingObj.tool_guide.file_extension
  });
  return response;
});

// async slice for downloading legal document
export const getLegalDocument = createAsyncThunk(
  'getLegalDocument',
  async () => {
    const response = await fetchDocument({
      url: downloadDocMappingObj.legal_doc_guide.url,
      fileName: downloadDocMappingObj.legal_doc_guide.file_name,
      fileExtension: downloadDocMappingObj.legal_doc_guide.file_extension
    });
    return response;
  }
);

// async slice for fetching module id data
export const getModuleIdData = createAsyncThunk('getModuleIdData', async () => {
  const response = await fetchModuleIDData();
  return response;
});

// asycn slice for marking favorites
export const toggleFavorite = createAsyncThunk(
  'toggleFavorite',
  async (args: {
    identifier: string;
    identifier_pk_id: string;
    is_favourite: boolean;
    sourceID: number;
  }) => {
    const response = await toggleFavoriteItem(args);
    return response;
  }
);

// async slice for fetching favorite list
export const getFavoriteList = createAsyncThunk(
  'getFavoriteList',
  async (args: { sourceId: number }) => {
    const response = await fetchFavoriteList(args.sourceId);
    return response;
  }
);

// async slice for recording recently opened reports
export const recordRecentOpenReport = createAsyncThunk(
  'recordRecentOpenReport',
  async (args: {
    identifier: string;
    identifier_pk_id: string;
    sourceID: number;
  }) => {
    const response = await recordRecentlyOpenedReport(args);
    return response.data;
  }
);

// async slice for fetching announcements list
export const getAnnouncementMapping = createAsyncThunk(
  'getAnnouncementMapping',
  async (args: { sourceId: number }) => {
    const response = await fetchAnnouncementMapping(args.sourceId);
    return response.data;
  }
);

// async slice for fetching rcently opened list
export const getRecentlyOpenedList = createAsyncThunk(
  'getRecentlyOpenedList',
  async (args: { sourceId: number }) => {
    const response = await fetchRecentlyOpenedList(args.sourceId);
    return response.data;
  }
);

// async slice for fetching quick start user profile data
export const getQuickStartUserProfileData = createAsyncThunk(
  'getQuickStartUserProfileData',
  async (args: { sourceId: number }) => {
    const response = await fetchQuickStartUserProfileData(args.sourceId);
    return response;
  }
);

// async slice for fetching quick start user category data
export const getQuickStartUserCategoryData = createAsyncThunk(
  'getQuickStartUserCategoryData',
  async (args: { sourceId: number }) => {
    const response = await fetchQuickStartUserCategoryData(args.sourceId);
    return response.data;
  }
);

// async slice for fetching product information - features, release notes
export const getProductFeatureList = createAsyncThunk(
  'getProductFeatureList',
  async (args: { sourceId: number }) => {
    const response = await fetchReleaseNotesData(args.sourceId);
    return response;
  }
);

// async slice for fetching search results
export const getSearchResults = createAsyncThunk(
  'getSearchResults',
  async (args: { sourceId: number }) => {
    const response = await fetchSearchResults(args.sourceId);
    return response.data;
  }
);
// async slice for fetching product information - data maturity
export const getMarketMaturityData = createAsyncThunk(
  'getMarketMaturityData',
  async (args: { sourceId: number }) => {
    const response = await fetchMarketMaturity(args.sourceId);
    return response;
  }
);

export const setActiveBottomTab = createAsyncThunk(
  'setActiveBottomTab', // Name of the action
  async (tabName: string) => tabName // Return the payload which is the tab name
);

const initialState: ILandingState = {
  fetchingCards: false,
  fetchingSegmentDetails: false,
  segmentDetailsData: {},
  fetchingSourceList: false,
  fetchingHomePageMetaData: false,
  isUserGuideDownloading: false,
  isLegalDocumentDownloading: false,
  fetchingModuleIdData: false,
  isFavoriteListFetching: true,
  isRecentlyOpenedListFetching: true,
  isUserProfileDataFetching: true,
  userProfileData: [],
  isProductFeatureLoading: false,
  productFeatureList: {
    release_notes_data: {},
    mapping_types_data: []
  },
  isMarketMaturityDataLoading: false,
  marketMaturityCardsData: {},
  isUserCategoryDataFetching: true,
  userCategoryData: [],
  favouriteListData: {
    data: [],
    identifier: {}
  },
  recentlyOpenedList: [],
  isAnnouncementLoading: false,
  isSearchDataLoading: false,
  searchResultsData: {
    data: [],
    identifier: {}
  },
  searchQueryInput: '',
  announcementData: [],
  moduleIdData: {},
  sourceFilterData: [],
  homePageMetaData: {
    learning_content: {
      alias: '',
      data: []
    },
    tool_information: [],
    business_questions: []
  },
  fetchingBusinessQuestionPopData: false,
  businessQuestionPopupData: [],
  fetchingEnhancementPopupData: false,
  enhancementPopupData: {
    release_notes_details: {
      alias: '',
      version_list: []
    },
    release_notes_data_2: [],
    legend_object: []
  },
  fetchingMarketMaturityData: false,
  marketMaturityData: {
    market_maturity_table: { headers: [], rows: [] },
    label: '',
    maturity_level: 0,
    alias: '',
    click_text: ''
  },
  cards: [],
  activeBottomTab: ''
};

const landingSlice = createSlice({
  name: 'landing',
  initialState,
  reducers: {
    setSearchQueryInput(state, action) {
      state.searchQueryInput = action.payload;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchCards.pending, (state) => {
        state.fetchingCards = true;
      })
      .addCase(fetchCards.rejected, (state) => {
        state.fetchingCards = false;
      })
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.fetchingCards = false;
        state.cards = action.payload;
      })
      .addCase(getSegmentDetails.pending, (state) => {
        state.fetchingSegmentDetails = true;
      })
      .addCase(getSegmentDetails.rejected, (state) => {
        state.fetchingSegmentDetails = false;
      })
      .addCase(getSegmentDetails.fulfilled, (state, action) => {
        state.fetchingSegmentDetails = false;
        state.segmentDetailsData = action.payload;
      })
      .addCase(getSourceList.pending, (state) => {
        state.fetchingSourceList = true;
      })
      .addCase(getSourceList.rejected, (state) => {
        state.fetchingSourceList = false;
      })
      .addCase(getSourceList.fulfilled, (state, action) => {
        state.fetchingSourceList = false;
        state.sourceFilterData = action.payload.data;
        // state.sourceNewFilterData = action.payload.data;
      })
      .addCase(getHomePageMetaData.pending, (state) => {
        state.fetchingHomePageMetaData = true;
      })
      .addCase(getHomePageMetaData.rejected, (state) => {
        state.fetchingHomePageMetaData = false;
      })
      .addCase(getHomePageMetaData.fulfilled, (state, action) => {
        state.fetchingHomePageMetaData = false;

        state.homePageMetaData = action.payload;

        // storing market maturity data
        state.marketMaturityData =
          action.payload.tool_information.data.market_maturity_details;
      })
      // START Business question popup data
      .addCase(getBusinessQuestionPopupData.pending, (state) => {
        state.fetchingBusinessQuestionPopData = true;
      })
      .addCase(getBusinessQuestionPopupData.rejected, (state) => {
        state.fetchingBusinessQuestionPopData = false;
      })
      .addCase(getBusinessQuestionPopupData.fulfilled, (state, action) => {
        state.fetchingBusinessQuestionPopData = false;
        state.businessQuestionPopupData = action.payload;
      })

      .addCase(getEnhancementPopupData.pending, (state) => {
        state.fetchingEnhancementPopupData = true;
      })
      .addCase(getEnhancementPopupData.rejected, (state) => {
        state.fetchingEnhancementPopupData = false;
      })
      .addCase(getEnhancementPopupData.fulfilled, (state, action) => {
        state.fetchingEnhancementPopupData = false;
        state.enhancementPopupData = action.payload;
      })

      .addCase(getModulesList.pending, (state) => {
        state.fetchingSourceList = true;
      })
      .addCase(getModulesList.rejected, (state) => {
        state.fetchingSourceList = false;
      })
      .addCase(getModulesList.fulfilled, (state, action) => {
        state.fetchingSourceList = false;
        state.sourceFilterData = action.payload.data;
      })

      .addCase(getUserGuide.pending, (state) => {
        state.isUserGuideDownloading = true;
      })
      .addCase(getUserGuide.rejected, (state) => {
        state.isUserGuideDownloading = false;
      })
      .addCase(getUserGuide.fulfilled, (state, action) => {
        state.isUserGuideDownloading = false;
      })

      .addCase(getLegalDocument.pending, (state) => {
        state.isLegalDocumentDownloading = true;
      })
      .addCase(getLegalDocument.rejected, (state) => {
        state.isLegalDocumentDownloading = false;
      })
      .addCase(getLegalDocument.fulfilled, (state, action) => {
        state.isLegalDocumentDownloading = false;
      })

      .addCase(getModuleIdData.pending, (state) => {
        state.fetchingModuleIdData = true;
      })
      .addCase(getModuleIdData.rejected, (state) => {
        state.fetchingModuleIdData = false;
      })
      .addCase(getModuleIdData.fulfilled, (state, action) => {
        state.fetchingModuleIdData = false;
        state.moduleIdData = action.payload;
      })

      .addCase(getFavoriteList.pending, (state) => {
        state.isFavoriteListFetching = true;
      })
      .addCase(getFavoriteList.rejected, (state) => {
        state.isFavoriteListFetching = false;
      })
      .addCase(getFavoriteList.fulfilled, (state, action) => {
        state.isFavoriteListFetching = false;
        state.favouriteListData = action.payload.data;
      })

      .addCase(getRecentlyOpenedList.pending, (state) => {
        state.isRecentlyOpenedListFetching = true;
      })
      .addCase(getRecentlyOpenedList.rejected, (state) => {
        state.isRecentlyOpenedListFetching = false;
      })
      .addCase(getRecentlyOpenedList.fulfilled, (state, action) => {
        state.isRecentlyOpenedListFetching = false;
        state.recentlyOpenedList = action.payload;
      })
      .addCase(recordRecentOpenReport.pending, (state) => {
        state.isRecentlyOpenedListFetching = true;
      })
      .addCase(recordRecentOpenReport.rejected, (state) => {
        state.isRecentlyOpenedListFetching = false;
      })
      .addCase(recordRecentOpenReport.fulfilled, (state, action) => {
        state.isRecentlyOpenedListFetching = false;
        state.recentlyOpenedList = action.payload;
      })
      .addCase(toggleFavorite.pending, (state) => {
        state.isFavoriteListFetching = true;
      })
      .addCase(toggleFavorite.rejected, (state) => {
        state.isFavoriteListFetching = false;
      })
      .addCase(toggleFavorite.fulfilled, (state, action) => {
        state.isFavoriteListFetching = false;
        state.favouriteListData = action.payload;
      })

      .addCase(getQuickStartUserProfileData.pending, (state) => {
        state.isUserProfileDataFetching = true;
      })
      .addCase(getQuickStartUserProfileData.rejected, (state) => {
        state.isUserProfileDataFetching = false;
      })
      .addCase(getQuickStartUserProfileData.fulfilled, (state, action) => {
        state.isUserProfileDataFetching = false;
        state.userProfileData = action.payload;
      })
      .addCase(getProductFeatureList.pending, (state) => {
        state.isProductFeatureLoading = true;
      })
      .addCase(getProductFeatureList.fulfilled, (state, action) => {
        state.isProductFeatureLoading = false;
        state.productFeatureList = action.payload;
      })
      .addCase(getProductFeatureList.rejected, (state) => {
        state.isProductFeatureLoading = false;
      })
      .addCase(getMarketMaturityData.pending, (state) => {
        state.isMarketMaturityDataLoading = true;
      })
      .addCase(getMarketMaturityData.fulfilled, (state, action) => {
        state.isMarketMaturityDataLoading = false;
        state.marketMaturityCardsData = action.payload;
      })
      .addCase(getMarketMaturityData.rejected, (state) => {
        state.isMarketMaturityDataLoading = false;
      })
      .addCase(getQuickStartUserCategoryData.pending, (state) => {
        state.isUserCategoryDataFetching = true;
      })
      .addCase(getQuickStartUserCategoryData.rejected, (state) => {
        state.isUserCategoryDataFetching = false;
      })
      .addCase(getQuickStartUserCategoryData.fulfilled, (state, action) => {
        state.isUserCategoryDataFetching = false;
        state.userCategoryData = action.payload;
      })
      .addCase(getAnnouncementMapping.pending, (state) => {
        state.isAnnouncementLoading = true;
      })
      .addCase(getAnnouncementMapping.fulfilled, (state, action) => {
        state.isAnnouncementLoading = false;
        state.announcementData = action.payload;
      })
      .addCase(getAnnouncementMapping.rejected, (state) => {
        state.isAnnouncementLoading = false;
      })

      .addCase(getSearchResults.pending, (state) => {
        state.isSearchDataLoading = true;
      })
      .addCase(getSearchResults.rejected, (state) => {
        state.isSearchDataLoading = false;
      })
      .addCase(getSearchResults.fulfilled, (state, action) => {
        state.isSearchDataLoading = false;
        state.searchResultsData = action.payload;
      })
      .addCase(setActiveBottomTab.fulfilled, (state, action) => {
        state.activeBottomTab = action.payload;
      })
});

export const { setSearchQueryInput } = landingSlice.actions;

export default landingSlice;
